import { useLeads } from "../lib/tenant-api";
import { formatRelative } from "../lib/format";
import {
  EmptyState,
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

export default function Leads() {
  const { data, isLoading, error } = useLeads();
  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
        <p className="mt-1 text-sm text-slate-500">
          Captured during conversations. Qualified leads also email you in real time.
        </p>
      </div>
      {isLoading && <LoadingRow />}
      {error && <ErrorBanner message={(error as Error).message} />}
      {data && data.length === 0 && (
        <EmptyState
          icon="🎯"
          title="No leads yet"
          hint="The receptionist creates a lead any time it captures a name + phone or email."
        />
      )}
      {data && data.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left font-medium px-4 py-2">Name</th>
                <th className="text-left font-medium px-4 py-2">Contact</th>
                <th className="text-left font-medium px-4 py-2">Interest</th>
                <th className="text-left font-medium px-4 py-2">Qualified</th>
                <th className="text-left font-medium px-4 py-2">Captured</th>
              </tr>
            </thead>
            <tbody>
              {data.map((l) => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-900">
                    {l.name || (
                      <span className="text-slate-400">(unnamed)</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700">{l.phone || "—"}</div>
                    {l.email && (
                      <div className="text-xs text-slate-500">{l.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {l.service_interest || (
                      <span className="text-slate-400">—</span>
                    )}
                    {l.preferred_time && (
                      <div className="text-xs text-slate-500">
                        prefers {l.preferred_time}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <QualifiedBadge value={l.qualified} reason={l.qualification_reason} />
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {formatRelative(l.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function QualifiedBadge({
  value,
  reason,
}: {
  value: number;
  reason: string | null;
}) {
  const config =
    value === 1
      ? { label: "qualified", cls: "bg-emerald-100 text-emerald-700" }
      : value === 0
        ? { label: "not qualified", cls: "bg-red-100 text-red-700" }
        : { label: "unknown", cls: "bg-slate-100 text-slate-600" };
  return (
    <div>
      <span
        className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${config.cls}`}
      >
        {config.label}
      </span>
      {reason && (
        <div className="text-xs text-slate-500 mt-1 line-clamp-2 max-w-xs">
          {reason}
        </div>
      )}
    </div>
  );
}
